import {
  Archive,
  ArrowRightLeft,
  Award,
  BadgeCheck,
  Ban,
  BatteryLow,
  BookOpen,
  Calendar,
  CalendarClock,
  CalendarX,
  Check,
  Circle,
  CircleCheck,
  CirclePause,
  CirclePlay,
  CircleX,
  Clock,
  Cloud,
  File,
  FilePen,
  Flag,
  Gavel,
  Hourglass,
  Inbox,
  Loader,
  Lock,
  LogIn,
  LogOut,
  MailClock,
  MessageCircle,
  Minus,
  Pencil,
  Radio,
  RotateCcw,
  Scale,
  Search,
  Send,
  ShieldCheck,
  ShieldOff,
  Slash,
  Star,
  ThumbsUp,
  Ticket,
  Timer,
  Trash,
  TriangleAlert,
  Undo2,
  UserX,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeAppearance, BadgeTone } from './Badge';

export interface StatusSpec {
  tone: BadgeTone;
  appearance: BadgeAppearance;
  icon: LucideIcon;
  /** Only "live" pulses (design system §4.11). */
  pulse?: boolean;
  /** Spinner-style icon (recording processing). */
  spin?: boolean;
  /** Gold icon on a success badge (verified teacher). */
  goldIcon?: boolean;
}

const s = (tone: BadgeTone, icon: LucideIcon, appearance: BadgeAppearance = 'tinted', extra: Partial<StatusSpec> = {}): StatusSpec => ({
  tone,
  icon,
  appearance,
  ...extra,
});

/**
 * Colour + icon for every state of every canonical state machine (brief §5,
 * design system §4.11). Labels live in the common catalog as `status.<machine>.<state>`.
 */
export const STATUS_MACHINES = {
  user: {
    pending_verification: s('warning', MailClock),
    active: s('success', CircleCheck),
    suspended: s('danger', Ban),
    archived: s('neutral', Archive),
    locked: s('danger', Lock, 'solid'),
  },
  course: {
    draft: s('neutral', FilePen),
    published: s('success', CircleCheck),
    archived: s('neutral', Archive),
  },
  organization: {
    active: s('success', CircleCheck),
    suspended: s('danger', Ban),
    archived: s('neutral', Archive),
  },
  content_source: {
    draft: s('neutral', FilePen),
    licensed: s('info', BadgeCheck),
    published: s('success', CircleCheck),
    unpublished: s('neutral', Archive),
    expired: s('warning', CalendarX),
  },
  membership: {
    invited: s('warning', MailClock),
    active: s('success', CircleCheck),
    suspended: s('danger', Ban),
  },
  teacher_verification: {
    draft: s('neutral', FilePen),
    submitted: s('info', Send),
    under_review: s('info', Search),
    verified: s('success', BadgeCheck, 'tinted', { goldIcon: true }),
    rejected: s('danger', CircleX),
    suspended: s('danger', Ban),
  },
  enrollment: {
    requested: s('info', Inbox),
    approved: s('primary', ThumbsUp),
    pending_payment: s('warning', Wallet),
    active: s('success', CircleCheck),
    paused: s('neutral', CirclePause),
    completed: s('primary', Flag),
    certified: s('gold', Award),
    expired: s('neutral', CalendarX),
    cancelled: s('danger', CircleX, 'outline'),
    withdrawn: s('danger', LogOut, 'outline'),
    transferred: s('info', ArrowRightLeft, 'outline'),
  },
  entitlement: {
    pending: s('warning', Hourglass),
    active: s('success', CircleCheck),
    exhausted: s('warning', BatteryLow),
    expired: s('neutral', CalendarX),
    revoked: s('danger', Slash),
  },
  payment_record: {
    pending: s('warning', Clock),
    confirmed: s('success', CircleCheck),
    partially_refunded: s('info', RotateCcw),
    refunded: s('neutral', RotateCcw),
    rejected: s('danger', CircleX),
    cancelled: s('neutral', X),
  },
  activation_code: {
    issued: s('info', Ticket),
    redeemed: s('success', Check),
    expired: s('neutral', CalendarX),
    revoked: s('danger', Slash),
  },
  session_occurrence: {
    scheduled: s('neutral', Calendar, 'outline'),
    teacher_joined: s('info', LogIn),
    student_joined: s('info', LogIn),
    live: s('danger', Radio, 'tinted', { pulse: true }),
    completed: s('success', CircleCheck),
    cancelled_by_teacher: s('danger', CircleX, 'outline'),
    cancelled_by_student: s('danger', CircleX, 'outline'),
    cancelled_by_admin: s('danger', CircleX, 'outline'),
    no_show_student: s('warning', UserX),
    no_show_teacher: s('warning', UserX),
    rescheduled: s('info', CalendarClock),
  },
  attendance: {
    present: s('success', Check),
    late: s('warning', Clock),
    left_early: s('warning', LogOut),
    absent: s('danger', X),
    excused: s('info', ShieldCheck),
    no_show: s('danger', UserX, 'outline'),
    teacher_absent: s('warning', UserX, 'outline'),
    cancelled: s('neutral', Minus),
  },
  recording: {
    pending_provider: s('neutral', Cloud),
    processing: s('info', Loader, 'tinted', { spin: true }),
    available: s('success', CirclePlay),
    expiring: s('warning', Timer),
    expired: s('neutral', Trash),
    deleted: s('neutral', Trash),
    failed: s('danger', TriangleAlert),
    legal_hold: s('info', Lock, 'solid'),
  },
  assignment: {
    draft: s('neutral', FilePen),
    published: s('success', CircleCheck),
    closed: s('neutral', Lock),
    archived: s('neutral', Archive),
  },
  exam: {
    draft: s('neutral', FilePen),
    published: s('success', CircleCheck),
    closed: s('neutral', Lock),
    archived: s('neutral', Archive),
  },
  reschedule_request: {
    pending: s('warning', Hourglass),
    approved: s('success', CircleCheck),
    rejected: s('danger', CircleX, 'outline'),
    withdrawn: s('neutral', Undo2),
    expired: s('neutral', CalendarX),
  },
  announcement: {
    scheduled: s('info', CalendarClock),
    published: s('success', CircleCheck),
    withdrawn: s('neutral', Undo2),
    expired: s('neutral', CalendarX),
  },
  erasure_request: {
    pending: s('warning', Hourglass),
    approved: s('info', ThumbsUp),
    declined: s('neutral', CircleX),
    cancelled: s('neutral', X),
    completed: s('danger', Trash, 'solid'),
  },
  assignment_submission: {
    not_started: s('neutral', Circle),
    in_progress: s('info', Pencil),
    submitted: s('primary', Send),
    under_review: s('info', Search),
    graded: s('success', CircleCheck),
    returned_for_resubmission: s('warning', Undo2),
    late: s('warning', Clock, 'outline'),
    missed: s('danger', CircleX),
  },
  exam_attempt: {
    not_started: s('neutral', Circle),
    in_progress: s('info', Pencil),
    submitted: s('primary', Send),
    grading: s('info', Search),
    graded: s('success', Check, 'outline'),
    published: s('success', CircleCheck),
    abandoned: s('neutral', LogOut),
    invalidated: s('danger', ShieldOff),
    appealed: s('warning', Scale),
    appeal_resolved: s('info', Gavel),
  },
  hifz: {
    not_started: s('hifz-none', Circle),
    learning: s('hifz-learning', BookOpen),
    memorized: s('hifz-memorized', Check),
    needs_revision: s('hifz-weak', RotateCcw),
    mastered: s('hifz-mastered', Star),
  },
  certificate: {
    draft: s('neutral', File),
    issued: s('gold', Award),
    revoked: s('danger', ShieldOff, 'solid'),
  },
  ticket: {
    open: s('info', Inbox),
    in_progress: s('primary', Loader),
    waiting_on_user: s('warning', MessageCircle),
    resolved: s('success', Check),
    closed: s('neutral', Archive),
    reopened: s('warning', RotateCcw, 'outline'),
  },
} as const satisfies Record<string, Record<string, StatusSpec>>;

export type StatusMachine = keyof typeof STATUS_MACHINES;
export type StatusOf<M extends StatusMachine> = keyof (typeof STATUS_MACHINES)[M] & string;

export function statusSpec(machine: StatusMachine, state: string): StatusSpec | undefined {
  const table: Record<string, StatusSpec> = STATUS_MACHINES[machine];
  return table[state];
}

export function statusLabelKey(machine: StatusMachine, state: string): string {
  return `status.${machine}.${state}`;
}
